// //api route request
import axios, { AxiosResponse, AxiosError } from "axios";
import { getToken } from "../services/authService";
import Toast from "../Components/Common/Toast";

export const baseURL: string = process.env.REACT_APP_BASE_URL || "";

interface ErrorResponse {
  statusMessage?: string;
  error?: string;
  statusCode: number;
}

/* ── Token helpers ───────────────────────────────────────────────── */
const isTokenExpired = (token: string | null): boolean => {
  if (!token) return true;
  try {
    const payload = JSON.parse(atob(token.split(".")[1]));
    return Date.now() >= payload.exp * 1000;
  } catch {
    return true;
  }
};

/* ── Navigation callback ─────────────────────────────────────────── */
let navigateCallback: ((path: string) => void) | null = null;

export const setNavigateCallback = (callback: (path: string) => void) => {
  navigateCallback = callback;
};

const handleAuthFailure = () => {
  localStorage.clear();
  if (navigateCallback) {
    navigateCallback("/login");
  } else {
    // Fallback to window.location if navigate callback not set
    window.location.href = "/login";
  }
};

/* ── Refresh token queue ─────────────────────────────────────────── */
let isRefreshing = false;
let failedQueue: Array<{
  resolve: (token: string) => void;
  reject: (reason?: unknown) => void;
}> = [];

const processQueue = (error: unknown, token: string | null = null) => {
  failedQueue.forEach(({ resolve, reject }) => {
    if (error) reject(error);
    else resolve(token!);
  });
  failedQueue = [];
};

const refreshAccessToken = async (): Promise<string> => {
  const userData = localStorage.getItem("userData");
  if (!userData) throw new Error("No user data found");

  const { refreshToken } = JSON.parse(userData);
  if (!refreshToken) throw new Error("No refresh token found");

  const res = await axios.post(`${baseURL}/refresh-token`, {
    refreshToken,
  });

  if (res.status !== 200) throw new Error("Refresh token failed");

  const { token, refreshToken: newRefreshToken } = res.data?.data ?? {};
  if (!token) throw new Error("No access token returned");

  // Update localStorage with new tokens
  const updatedUserData = JSON.parse(userData);
  updatedUserData.token = token;
  if (newRefreshToken) updatedUserData.refreshToken = newRefreshToken;
  localStorage.setItem("userData", JSON.stringify(updatedUserData));

  return token;
};

async function makeRequest(
  method: string,
  url: string,
  data?: any,
  token = "",
  isRetry = false
): Promise<AxiosResponse | undefined> {
  try {
    const response = await axios({
      method,
      url,
      data,
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });

    return response;
  } catch (error) {
    if (axios.isAxiosError(error)) {
      const axiosError = error as AxiosError<ErrorResponse>;
      
      // Handle 401 errors with token refresh
      if (axiosError.response?.status === 401 && !isRetry) {
        const auth = getToken();
        const refreshToken = auth?.refreshToken;
        
        if (!refreshToken || isTokenExpired(refreshToken)) {
          handleAuthFailure();
        }

        // If already refreshing, queue this request
        if (isRefreshing) {
          return new Promise((resolve, reject) => {
            failedQueue.push({
              resolve: async (newToken) => {
                try {
                  const retryResponse = await makeRequest(method, url, data, newToken, true);
                  resolve(retryResponse);
                } catch (err) {
                  reject(err);
                }
              },
              reject,
            });
          });
        }

        // Start refresh process
        isRefreshing = true;
        try {
          const newToken = await refreshAccessToken();
          isRefreshing = false;
          processQueue(null, newToken);
          
          // Retry the original request with new token
          return makeRequest(method, url, data, newToken, true);
        } catch (refreshError) {
          isRefreshing = false;
          processQueue(refreshError, null);
          handleAuthFailure();
          throw refreshError;
        }
      }

      // Handle other errors
      if (axiosError.response?.data?.statusMessage) {
        const errorMessage = axiosError.response.data.statusMessage;
        const errorCode = axiosError.response.data.statusCode;
        
        if (errorCode === 401) {
          handleAuthFailure();
        }
        
        Toast(errorMessage, "error");
      }
    } else {
      Toast("An unexpected error occurred.", "error");
    }
  }
}

export async function getRequest(
  url: string
): Promise<AxiosResponse | undefined> {
  const auth = getToken();
  return makeRequest("get", url, undefined, auth?.token);
}

export async function postRequest(
  url: string,
  data?: any
): Promise<AxiosResponse | undefined> {
  const auth = getToken();
  return makeRequest("post", url, data, auth?.token);
}

export async function putRequest(
  url: string,
  data: any
): Promise<AxiosResponse | undefined> {
  const auth = getToken();
  return makeRequest("put", url, data, auth?.token);
}

export async function patchRequest(
  url: string,
  data: any
): Promise<AxiosResponse | undefined> {
  const auth = getToken();
  return makeRequest("patch", url, data, auth?.token);
}

export async function deleteRequest(
  url: string,
  data?: any
): Promise<AxiosResponse | undefined> {
  const auth = getToken();
  return makeRequest("delete", url, data, auth?.token);
}

export async function multiPostRequest(
  url: string,
  data: FormData
): Promise<AxiosResponse | undefined> {
  const auth = getToken();
  try {
    const response = await axios.post(url, data, {
      headers: {
        Authorization: `Bearer ${auth?.token}`,
        "Content-Type": "multipart/form-data",
      },
    });
    return response;
  } catch (error) {
    if (error instanceof AxiosError) {
      const errorMessage =
        error.response?.data?.statusMessage || "Something Went Wrong..!";

      Toast(errorMessage, "error");
    } else {
      Toast("An unexpected error occurred.", "error");
    }
  }
}

export async function multiPartPUTRequest(
  url: string,
  data: FormData
): Promise<AxiosResponse | undefined> {
  const auth = getToken();
  try {
    const response = await axios.put(url, data, {
      headers: {
        Authorization: `Bearer ${auth?.token}`,
        "Content-Type": "multipart/form-data",
      },
    });

    return response;
  } catch (error) {
    if (error instanceof AxiosError) {
      const errorMessage =
        error.response?.data?.statusMessage || "Something Went Wrong..!";
      Toast(errorMessage, "error");
    } else {
      Toast("An unexpected error occurred.", "error");
    }
  }
}
